import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react';

interface GlobalCommandPaletteContextType {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    toggle: () => void;
}

const GlobalCommandPaletteContext = createContext<GlobalCommandPaletteContextType | undefined>(
    undefined
);

export function GlobalCommandPaletteProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);

    // Global shortcut ⌘K / Ctrl+K
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen((prev) => !prev);
            }
        };

        document.addEventListener('keydown', handleKeyDown, { capture: true });

        return () => {
            document.removeEventListener('keydown', handleKeyDown, { capture: true });
        };
    }, []);

    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
    const contextValue = useMemo(() => ({ isOpen, setIsOpen, toggle }), [isOpen, toggle]);

    return (
        <GlobalCommandPaletteContext.Provider value={contextValue}>
            {children}
        </GlobalCommandPaletteContext.Provider>
    );
}

export function useGlobalCommandPaletteContext() {
    const context = useContext(GlobalCommandPaletteContext);
    if (context === undefined) {
        throw new Error(
            'useGlobalCommandPaletteContext must be used within a GlobalCommandPaletteProvider'
        );
    }
    return context;
}
